import type { ParsedUrlQuery } from 'querystring';
import { stringify } from 'querystring';

import type { CurrentNetworkContext, Message, Thread } from '@ms/yammer-data/dist/state/types';
import type { SearchLocationParameters } from '@ms/yammer-search-types';
import type { LocationDescriptor } from 'history';

import type { GetBaseUrlProps } from '../location/getBaseUrl';
import { getBaseUrl } from '../location/getBaseUrl';

import { campaignUrl } from './campaigns';
import { corpCommsProjectOverviewPageUrl } from './communicationsDashboard';
import { groupFeedRedirectPage } from './groups';
import { rootPageUrl } from './rootRoutes';
import { makeSearchQueryStringParameters } from './search';
import type { StateWithPrevPathname, ThreadPageQueryOptions } from './threads';
import { userPage } from './users';

export const articleBasePath = '/articles';
export const articlePageMatchPath: string = `${articleBasePath}/:threadId/:topLevelMessageId?/:secondLevelMessageId?`;
export const articleEditPageMatchPath: string = `${articleBasePath}/:threadId/edit`;
export const articleNewMatchPath: string = `${articleBasePath}/new`;
export const articleNewBasePath: string = `${articleBasePath}/new`;

export interface PageArticleMatchParams {
  readonly threadId: Thread['id'];
  readonly topLevelMessageId?: Thread['threadStarterId'];
  readonly secondLevelMessageId?: Message['id'];
}

export type ArticlePageQueryOptions = ThreadPageQueryOptions;

const getLocationSearchQueryString = (queryOptions?: ArticlePageQueryOptions) =>
  queryOptions ? `?${stringify(queryOptions as unknown as ParsedUrlQuery)}` : undefined;

type ArticlePage = (
  articleId: Thread['id'],
  queryOptions?: ArticlePageQueryOptions,
  networkContext?: CurrentNetworkContext
) => string;
export const articlePage: ArticlePage = (articleId, queryOptions, networkContext) => {
  const queryString = getLocationSearchQueryString(queryOptions) ?? '';

  if (networkContext) {
    return `/${networkContext.networkPath}${articleBasePath}/${articleId}${queryString}`;
  }

  return `${articleBasePath}/${articleId}${queryString}`;
};

type ArticleMessagePage = (messagePath: Message['id'][], queryOptions?: ArticlePageQueryOptions) => string;
export const articleMessagePage: ArticleMessagePage = (messagePath, queryOptions) => {
  const queryString = getLocationSearchQueryString(queryOptions) ?? '';

  return `${articleBasePath}/${messagePath.join('/')}${queryString}`;
};

type ArticleMessagePageWithPreviousPathname = (
  ids: Message['id'][],
  prevPathname: string,
  options?: ArticlePageQueryOptions
) => LocationDescriptor<StateWithPrevPathname>;
export const articleMessagePageWithPreviousPathname: ArticleMessagePageWithPreviousPathname = (
  ids,
  prevPathname,
  options
) => ({
  pathname: articleMessagePage(ids),
  search: getLocationSearchQueryString(options),
  state: { prevPathname },
});

interface ArticlePageNewUrlParams {
  readonly userId?: string;
  readonly groupId?: string | null;
  readonly campaignId?: string;
  readonly commsPlanId?: string;
  readonly scheduledAt?: string;
  readonly isFromUniversalCreateButton?: boolean;
}

type ArticlePageNewUrl = (params: ArticlePageNewUrlParams) => string;
export const articlePageNewUrl: ArticlePageNewUrl = ({
  userId,
  groupId,
  campaignId,
  commsPlanId,
  scheduledAt,
  isFromUniversalCreateButton,
}) => {
  const params: Record<string, string> = {};

  if (groupId) {
    params.groupId = groupId;
  }

  if (userId) {
    params.userId = userId;
  }

  if (campaignId) {
    params.campaignId = campaignId;
  }

  if (commsPlanId) {
    params.commsPlanId = commsPlanId;
  }

  if (scheduledAt) {
    params.scheduledAt = scheduledAt;
  }

  if (isFromUniversalCreateButton) {
    params.from = 'universalCreateButton';
  }

  const keys = Object.keys(params);
  const pathWithParams = keys.length > 0 ? `${articleNewBasePath}?${stringify(params)}` : articleNewBasePath;

  return pathWithParams;
};

type ArticlePageEditUrl = (threadId: Thread['id']) => string;
export const articlePageEditUrl: ArticlePageEditUrl = (threadId) => `${articleBasePath}/${threadId}/edit`;

type OnArticlePublishRedirectPath = (params: ArticlePageNewUrlParams) => string;
export const onArticlePublishRedirectPath: OnArticlePublishRedirectPath = ({
  userId,
  groupId,
  campaignId,
  commsPlanId,
}) => {
  if (commsPlanId) {
    return corpCommsProjectOverviewPageUrl(commsPlanId);
  }

  if (groupId) {
    return groupFeedRedirectPage(groupId);
  }

  if (userId) {
    return userPage(userId);
  }

  if (campaignId) {
    return campaignUrl(campaignId);
  }

  return rootPageUrl;
};

interface articleUrlProps {
  readonly threadId: string;
  readonly options?: ThreadPageQueryOptions;
  readonly currentNetworkContext?: CurrentNetworkContext;
  readonly host?: GetBaseUrlProps['host'];
}
type ArticleUrl = (props: articleUrlProps) => string;
export const articleUrl: ArticleUrl = ({ threadId, options, currentNetworkContext, host }) => {
  const baseUrl = getBaseUrl({ currentNetworkContext, host });

  return `${baseUrl}${articlePage(threadId, options)}`;
};

type ArticlePageWithSearchParameters = (id: Thread['id'], searchParams: SearchLocationParameters) => string;
export const articlePageWithSearchParameters: ArticlePageWithSearchParameters = (id, searchParams) => {
  const queryOptions = makeSearchQueryStringParameters(searchParams);

  return articlePage(id, queryOptions);
};
